import { useEffect, useRef, useState } from "react";
import { motion, useScroll, AnimatePresence, useMotionValueEvent } from "framer-motion";

// Importing your specific partner assets
import avellysLogo from "@/assets/Avellys.webp";
import broadwayLogo from "@/assets/broadway.webp";
import clawsPawsLogo from "@/assets/claws&paws.webp";

import NavigationHeader from "@/components/NavigationHeader";
import PageTransition from "@/components/PageTransition";
import MissionVision from "@/components/about/MissionVision";
import ServicesAbout from "@/components/about/ServicesAbout";
import Governance from "@/components/about/Governance";
import Footer from "../components/Footer";
import TrustedPartners from "@/components/about/TrustedPartners";
import SEO from "@/components/SEO";
import HomeAuroraBackground from "@/components/HomeAuroraBackground";
import aboutHeroOne from "@/assets/about-hero/about-hero-1.png";
import aboutHeroTwo from "@/assets/about-hero/about-hero-2.png";
import aboutHeroThree from "@/assets/about-hero/about-hero-3.png";
import aboutHeroFour from "@/assets/about-hero/about-hero-4.png";

import { PersonStanding, ChevronLeft, ChevronRight } from "lucide-react";

const headerSegments = [
  { text: "Passionate People.", gradient: "text-[#1A3A5C]" },
  {
    text: "Meaningful Work.",
    gradient:
      "bg-gradient-to-r from-[#9ecc48] via-teal-400 to-blue-500 bg-clip-text text-transparent",
  },
];

const premiumEase = [0.16, 1, 0.3, 1];

const heroContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 },
  },
};

const heroItem = {
  hidden: { opacity: 0, y: 60, rotateX: 15, filter: "blur(10px)" },
  show: {
    opacity: 1,
    y: 0,
    rotateX: 0,
    filter: "blur(0px)",
    transition: { duration: 1.2, ease: premiumEase },
  },
};

const heroTextItem = {
  hidden: { opacity: 0, y: 56, filter: "blur(10px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.9,
      ease: premiumEase,
    },
  },
};

const narrativeTexts = [
  <span key="0">
    <strong className="text-emerald-400">Vet Assist</strong>, developed by{" "}
    <strong className="text-emerald-400">Endsofttech Web Solutions</strong>, is a user-friendly
    veterinary practice management software designed to streamline operations and improve patient
    care.
  </span>,
  <span key="1">
    Tailored for the Philippine market, Vet Assist helps clinics transition from traditional methods
    to a modern, paperless system.
  </span>,
  <span key="2">
    Recognizing the need for a digital revolution, Endsofttech aimed to enhance operational
    efficiency where many clinics relied on outdated methods.
  </span>,
  <div key="3" className="flex flex-col items-start gap-8">
    <span>
      Driven by a vision to modernize, Vet Assist stands as a comprehensive solution for elevating
      care standards.
    </span>
    {/* 🌟 The White Bottom Glow */}
    <div className="absolute -bottom-2 left-1/2 h-6 w-3/4 -translate-x-1/2 rounded-full bg-white opacity-60 blur-xl transition-all duration-500 group-hover:h-8 group-hover:w-full group-hover:opacity-100 group-hover:blur-2xl" />

    <a
      href="https://endsofttech.com" // Update with the actual URL
      target="_blank"
      rel="noopener noreferrer"
      // Note: I moved the "group" class from here to the parent div above so the hover effects sync up!
      className="relative inline-flex items-center justify-center overflow-hidden rounded-full p-0.5 shadow-[0_0_20px_rgba(158,204,72,0.15)] transition-shadow duration-500 hover:shadow-[0_0_35px_rgba(158,204,72,0.4)] md:text-lg">
      {/* Spinning Gradient Border Layer */}
      <span className="absolute inset-[-1000%] animate-[spin_4s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#e2e8f0_0%,#9ecc48_50%,#0ea5e9_100%)] opacity-70 transition-opacity duration-500 group-hover:opacity-100" />

      {/* Dark Inner Button Content */}
      <span className="relative flex h-full w-full items-center gap-3 rounded-full bg-slate-900 px-6 py-3 text-base font-semibold tracking-wide text-slate-100 backdrop-blur-3xl transition-colors duration-300 group-hover:bg-slate-800 md:px-8 md:py-4">
        <span className="bg-linear-to-r from-white to-slate-300 bg-clip-text text-transparent">
          Explore Endsofttech
        </span>
        <svg
          className="h-5 w-5 text-[#9ecc48] transition-all duration-300 group-hover:translate-x-1.5 group-hover:scale-110 group-hover:text-teal-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"></line>
          <polyline points="12 5 19 12 12 19"></polyline>
        </svg>
      </span>
    </a>
  </div>,
];

const overlayTexts = [
  "Built on trust.",
  "Modernized Care.",
  "Future Focused.",
  "Pioneering Efficiency.",
];

const aboutHeroImages = [aboutHeroOne, aboutHeroTwo, aboutHeroThree, aboutHeroFour];

const About = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const manualSlideRef = useRef(false);
  const manualTimerRef = useRef(null);

  // Ref for the entire sticky track
  const trackRef = useRef(null);

  // Track scroll progress strictly within this section's bounds
  const { scrollYProgress } = useScroll({
    target: trackRef,
    offset: ["start start", "end end"],
  });

  // Update the active index based on scroll position (0 to 1)
  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    if (manualSlideRef.current) return;
    if (latest < 0.25) setActiveIndex(0);
    else if (latest < 0.5) setActiveIndex(1);
    else if (latest < 0.75) setActiveIndex(2);
    else setActiveIndex(3);
  });

  const setManualSlide = (nextIndex) => {
    manualSlideRef.current = true;
    if (manualTimerRef.current) {
      clearTimeout(manualTimerRef.current);
    }
    setActiveIndex(nextIndex);
    manualTimerRef.current = setTimeout(() => {
      manualSlideRef.current = false;
    }, 2400);
  };

  const handlePrevSlide = () => {
    setManualSlide((activeIndex - 1 + aboutHeroImages.length) % aboutHeroImages.length);
  };

  const handleNextSlide = () => {
    setManualSlide((activeIndex + 1) % aboutHeroImages.length);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    return () => {
      if (manualTimerRef.current) clearTimeout(manualTimerRef.current);
    };
  }, []);

  return (
    <PageTransition>
      <SEO page="about" />
      {/* CRITICAL FIX: Removed `overflow-hidden` from the main wrapper. 
        Any overflow hidden/auto/clip on a parent instantly breaks `position: sticky`. 
      */}
      <main
        className="relative min-h-screen w-full font-sans text-slate-900"
        style={{
          background: "linear-gradient(135deg, #dbeafe 0%, #e8f4ff 20%, #f0fdf4 60%, #dcfce7 100%)",
        }}>
        <HomeAuroraBackground
          showStars={false}
          showWavingLines={true}
          className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
        />

        <div className="relative z-10">
          <NavigationHeader />

          <section className="relative px-6 pt-32 pb-10 md:px-12 md:pt-48 md:pb-24">
            <div className="relative z-10 mx-auto max-w-350">
              <motion.div
                variants={heroContainer}
                initial="hidden"
                animate="show"
                className="flex flex-col items-start">
                <motion.span
                  variants={heroTextItem}
                  className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-5 py-2 text-sm font-medium text-emerald-800 shadow-sm backdrop-blur-sm transition-colors hover:bg-emerald-500/20 md:mb-8">
                  <PersonStanding className="h-4 w-4 text-emerald-500" />
                  WHO WE ARE
                </motion.span>

                <motion.h1
                  style={{ perspective: "1000px" }}
                  className="flex flex-col gap-0 text-4xl leading-[1.1] font-bold tracking-tighter sm:text-5xl md:text-7xl lg:text-[7rem]">
                  {headerSegments.map((seg, i) => (
                    <div key={i} className="-mb-8 overflow-hidden pb-8">
                      <motion.span variants={heroTextItem} className={`block pb-2 ${seg.gradient}`}>
                        {seg.text}
                      </motion.span>
                    </div>
                  ))}
                </motion.h1>
              </motion.div>
            </div>
          </section>

          {/* =========================================
              STICKY SCROLLING SECTION 
              ========================================= */}
          {/* Track Container: The height dictates how long the pin lasts */}
          <section ref={trackRef} className="relative z-10 h-[400vh] w-full bg-transparent">
            {/* The Sticky Container: Pins to the top of the screen */}
            <div className="sticky top-0 flex h-screen w-full items-start justify-center overflow-hidden overflow-y-auto px-4 pt-6 md:items-center md:px-12 md:pt-0">
              <div className="mx-auto grid w-full max-w-350 gap-4 sm:gap-6 lg:grid-cols-2 lg:gap-24">
                {/* Left Side: Image Container */}
                <div className="relative h-[32vh] w-full overflow-hidden rounded-3xl border border-white/10 bg-slate-900/50 shadow-2xl backdrop-blur-xl sm:h-[38vh] lg:h-[70vh] lg:rounded-[3rem]">
                  {aboutHeroImages.map((image, index) => (
                    <motion.img
                      key={image}
                      src={image}
                      aria-hidden={activeIndex !== index}
                      initial={false}
                      animate={{
                        opacity: activeIndex === index ? 1 : 0,
                        scale: activeIndex === index ? 1 : 1.02,
                        filter: activeIndex === index ? "blur(0px)" : "blur(2px)",
                      }}
                      transition={{ duration: 0.8, ease: premiumEase }}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  ))}
                  <div className="absolute inset-0 bg-linear-to-t from-slate-950/90 via-slate-900/40 to-transparent" />

                  {/* Fading Overlay Text inside the Image */}
                  <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-10">
                    <AnimatePresence mode="wait">
                      <motion.h2
                        key={activeIndex}
                        initial={{ y: 20, opacity: 0, filter: "blur(10px)" }}
                        animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
                        exit={{ y: -20, opacity: 0, filter: "blur(10px)" }}
                        transition={{ duration: 0.6, ease: premiumEase }}
                        className="text-xl font-bold text-white sm:text-2xl md:text-4xl lg:text-5xl">
                        {overlayTexts[activeIndex]}
                      </motion.h2>
                    </AnimatePresence>

                    <div className="absolute right-4 bottom-4 z-20 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={handlePrevSlide}
                        className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-white/20 bg-slate-900/45 text-white backdrop-blur-md transition-colors hover:bg-slate-900/70"
                        aria-label="Previous slide">
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        onClick={handleNextSlide}
                        className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-white/20 bg-slate-900/45 text-white backdrop-blur-md transition-colors hover:bg-slate-900/70"
                        aria-label="Next slide">
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Right Side: Text Content Container */}
                <div className="flex flex-col justify-center py-4 lg:py-0">
                  <span className="mb-4 inline-block text-xs font-black tracking-[0.3em] text-[#9ecc48] uppercase md:mb-6">
                    Who we are
                  </span>

                  {/* CSS Grid Stacking: Overlaps texts cleanly to prevent layout jumps */}
                  <div className="relative grid">
                    {narrativeTexts.map((text, index) => (
                      <div
                        key={index}
                        className="col-start-1 row-start-1 text-lg leading-snug font-medium tracking-tight text-slate-700 sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl"
                        style={{
                          opacity: activeIndex === index ? 1 : 0,
                          pointerEvents: activeIndex === index ? "auto" : "none",
                          visibility: activeIndex === index ? "visible" : "hidden",
                          transition: "opacity 0.6s cubic-bezier(0.16, 1, 0.3, 1), visibility 0.6s",
                        }}>
                        {text}
                      </div>
                    ))}
                  </div>

                  {/* Small badge footer underneath text */}
                  <div className="mt-8 flex items-start gap-4 border-t border-[#1A3A5C]/25 pt-6 sm:items-center md:mt-12 md:gap-5 md:pt-10">
                    <motion.div
                      whileHover={{ scale: 1.05, rotate: -5 }}
                      whileTap={{ scale: 0.95 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/20 bg-slate-800 shadow-lg shadow-blue-500/20 md:h-14 md:w-14">
                      <div className="absolute inset-0 bg-linear-to-tr from-[#9ecc48]/20 to-transparent" />
                      <svg
                        className="relative z-10 h-5 w-5 text-[#9ecc48] md:h-6 md:w-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        <path d="m9 12 2 2 4-4" />
                      </svg>
                    </motion.div>

                    <div className="flex flex-col">
                      <span className="text-[15px] font-bold tracking-tight text-slate-900 md:text-base">
                        Committed to Excellence
                      </span>
                      <span className="text-sm leading-snug font-medium text-slate-600">
                        Empowering veterinary clinics nationwide.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <MissionVision />
          <Governance />
          <TrustedPartners />
          <ServicesAbout />

          <Footer />
        </div>
      </main>
    </PageTransition>
  );
};

export default About;
